import { renumberQuestions } from "../../../utils/surveySchema";

// Build details skeleton or merge details for target based on source
export function buildMirroredDetails(type, srcDetails = {}, tgtDetails = undefined) {
    const d = {};
    const srcOpts = Array.isArray(srcDetails.options) ? srcDetails.options : [];
    const tgtOpts = Array.isArray(tgtDetails?.options) ? tgtDetails.options : [];
    
    // Merge options with proper handling of {value, label} structure
    // - Values are language-independent (copied from source)
    // - Labels are language-dependent (preserved from target if exists, empty if not)
    const mergedOpts = srcOpts.length
        ? srcOpts.map((srcOpt, idx) => {
            const tgtOpt = tgtOpts[idx];
            // If source is object with value/label
            if (typeof srcOpt === 'object' && srcOpt !== null && 'value' in srcOpt) {
                return {
                    value: srcOpt.value, // Always use value from source (language-independent)
                    label: (typeof tgtOpt === 'object' && tgtOpt !== null) ? (tgtOpt.label ?? '') : '' // Preserve target label if exists
                };
            }
            // Legacy: if source is plain string
            return tgtOpt ?? '';
          })
        : (Array.isArray(tgtDetails?.options) ? tgtDetails.options : undefined);

    // Handle selectOptions for OpenList (dropdown) - preserve both value and label structure
    const hasSelectArray = Array.isArray(srcDetails.selectOptions);
    const srcSelect = hasSelectArray ? srcDetails.selectOptions : [];
    const tgtSelect = Array.isArray(tgtDetails?.selectOptions) ? tgtDetails.selectOptions : [];
    const mergedSelect = srcSelect.length
        ? srcSelect.map((srcOpt, idx) => {
            const tgtOpt = tgtSelect[idx];
            if (typeof srcOpt === 'object' && srcOpt !== null) {
                return {
                    value: srcOpt.value ?? '', // Keep value from source
                    label: (typeof tgtOpt === 'object' && tgtOpt !== null) ? (tgtOpt.label ?? '') : '' // Language-dependent label
                };
            }
            return tgtOpt ?? { value: '', label: '' };
          })
        : (Array.isArray(tgtDetails?.selectOptions) ? tgtDetails.selectOptions : undefined);

    if (type === 'SC' || type === 'MC') {
        if (mergedOpts) d.options = mergedOpts;
        if (Array.isArray(srcDetails.textFields)) d.textFields = [...srcDetails.textFields];
        if (type === 'SC' && srcDetails.skipLogic) d.skipLogic = { ...srcDetails.skipLogic };
    } else if (type === 'Open') {
        d.answerType = srcDetails.answerType || 'str';
        if (mergedOpts) d.options = mergedOpts;
        if (Array.isArray(srcDetails.textFields)) d.textFields = [...srcDetails.textFields];
        if (Number.isFinite(srcDetails.min)) d.min = srcDetails.min;
        if (Number.isFinite(srcDetails.max)) d.max = srcDetails.max;
    } else if (type === 'OpenList') {
        d.answerType = srcDetails.answerType || 'str';
        if (mergedOpts) d.options = mergedOpts;
        if (srcSelect.length > 0) {
            d.selectOptions = mergedSelect || [];
        }
        if (Array.isArray(srcDetails.textFields)) d.textFields = [...srcDetails.textFields];
        if (Number.isFinite(srcDetails.min)) d.min = srcDetails.min;
        if (Number.isFinite(srcDetails.max)) d.max = srcDetails.max;
    } else if (type === 'SelectText') {
        if (mergedOpts) d.options = mergedOpts;
    } else if (type === 'Label') {
        // Label has no type-specific details; fall through to carry generic fields
    } else {
        Object.assign(d, srcDetails);
    }
    // Carry over generic, cross-type fields
    if (Object.prototype.hasOwnProperty.call(srcDetails, 'attachmentLabel') || Object.prototype.hasOwnProperty.call(tgtDetails || {}, 'attachmentLabel')) {
        d.attachmentLabel = srcDetails.attachmentLabel ?? tgtDetails?.attachmentLabel ?? '';
    }
    return d;
}

export function groupByPage(questions = []) {
    const map = new Map();
    (questions || []).forEach((q) => {
        const p = Number(q.pageNumber || 1);
        if (!map.has(p)) map.set(p, []);
        map.get(p).push(q);
    });
    return map;
}

export function enableLanguage(prevSurvey, enableLang, i18nLanguage, LANGS) {
    if (Object.prototype.hasOwnProperty.call(prevSurvey || {}, enableLang)) return prevSurvey;
    const next = { ...(prevSurvey || {}) };
    const curLng = i18nLanguage;
    const hasCur = prevSurvey && Object.prototype.hasOwnProperty.call(prevSurvey, curLng) && Array.isArray(prevSurvey?.[curLng]?.questionnaire);
    const sourceLang = hasCur
        ? curLng
        : (LANGS.find((L) => prevSurvey && Object.prototype.hasOwnProperty.call(prevSurvey, L) && Array.isArray(prevSurvey?.[L]?.questionnaire) && (prevSurvey[L].questionnaire.length > 0)) || curLng);
    const sourceQs = (prevSurvey?.[sourceLang]?.questionnaire || []);

    const srcMap = groupByPage(sourceQs);
    const pages = Array.from(srcMap.keys()).sort((a, b) => a - b);

    const mirrored = [];
    pages.forEach((p) => {
        const srcList = srcMap.get(p) || [];
        srcList.forEach((srcQ) => {
            mirrored.push({
                questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
                pageNumber: p,
                questionType: srcQ?.questionType || 'SC',
                questionContent: '',
                isMetric: !!srcQ?.isMetric,
                details: buildMirroredDetails(srcQ?.questionType || 'SC', srcQ?.details || {}),
            });
        });
    });

    next[enableLang] = { questionnaire: renumberQuestions(mirrored) };
    return next;
}

export function syncStructureOnLanguageSwitch(prevSurvey, curLang, LANGS) {
    const lastLang = (prevSurvey?.__lastLang || curLang);
    const enabled = LANGS.filter((l) => prevSurvey && Object.prototype.hasOwnProperty.call(prevSurvey, l));

    const sourceLang = (enabled.includes(curLang) && Array.isArray(prevSurvey?.[curLang]?.questionnaire))
        ? curLang
        : ((enabled.includes(lastLang) && Array.isArray(prevSurvey?.[lastLang]?.questionnaire))
            ? lastLang
            : (enabled.find((lng) => Array.isArray(prevSurvey?.[lng]?.questionnaire) && (prevSurvey[lng].questionnaire.length > 0)) || curLang));
    const sourceQs = prevSurvey?.[sourceLang]?.questionnaire || [];
    if (sourceQs.length === 0) return prevSurvey;

    const srcMap = groupByPage(sourceQs);
    const pages = Array.from(srcMap.keys()).sort((a, b) => a - b);

    const next = { ...prevSurvey };
    enabled.forEach((lng) => {
        if (lng === sourceLang) return;
        const tgtSection = next?.[lng] || {};
        const tgtQs = Array.isArray(tgtSection.questionnaire) ? tgtSection.questionnaire : [];
        const tgtMap = groupByPage(tgtQs);
        const merged = [];
        pages.forEach((p) => {
            const srcList = srcMap.get(p) || [];
            const tgtList = tgtMap.get(p) || [];
            const count = srcList.length;
            for (let i = 0; i < count; i++) {
                const srcQ = srcList[i];
                const existing = tgtList[i];
                if (existing) {
                    merged.push({
                        ...existing,
                        pageNumber: p,
                        questionType: srcQ?.questionType || 'SC',
                        isMetric: !!srcQ?.isMetric,
                        details: buildMirroredDetails(srcQ?.questionType || 'SC', srcQ?.details || {}, existing.details || {}),
                    });
                } else {
                    merged.push({
                        questionID: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
                        pageNumber: p,
                        questionType: srcQ?.questionType || 'SC',
                        questionContent: '',
                        isMetric: !!srcQ?.isMetric,
                        details: buildMirroredDetails(srcQ?.questionType || 'SC', srcQ?.details || {}, {}),
                    });
                }
            }
        });
        next[lng] = { ...(tgtSection || {}), questionnaire: merged };
    });

    next.__lastLang = curLang;
    return next;
}
